import { useState, useRef } from 'react';
import { useShare, buildShareUrl } from '@/hooks/useShare';
import { SocialShareMenu } from '@/components/SocialShareMenu';

interface ShareButtonProps {
  topicSlug: string;
  topicName: string;
  episodeId?: string;
  episodeTitle?: string;
  variant?: 'icon' | 'button' | 'secondary';
  className?: string;
}

export function ShareButton({
  topicSlug,
  topicName,
  episodeId,
  episodeTitle,
  variant = 'secondary',
  className = '',
}: ShareButtonProps) {
  const { share, canUseNativeShare } = useShare();
  const [showMenu, setShowMenu] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const url = buildShareUrl(topicSlug, episodeId);
  const title = episodeTitle || topicName;
  const description = episodeTitle ? `From ${topicName}` : topicName;

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();

    // On mobile/supported browsers, use native share
    if (canUseNativeShare) {
      await share({ title, text: description, url });
    } else {
      // Desktop: show social share menu
      setShowMenu((prev) => !prev);
    }
  };

  const handleCloseMenu = () => {
    setShowMenu(false);
  };

  const ShareIcon = () => (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
      />
    </svg>
  );

  if (variant === 'icon') {
    return (
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={handleShare}
          className={`rounded-full p-2 transition-colors text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 ${className}`}
          title="Share"
          aria-label={`Share ${title}`}
          aria-expanded={showMenu}
          aria-haspopup="menu"
        >
          <ShareIcon />
        </button>
        {showMenu && (
          <SocialShareMenu
            url={url}
            title={title}
            description={description}
            onClose={handleCloseMenu}
          />
        )}
      </div>
    );
  }

  if (variant === 'button') {
    return (
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={handleShare}
          className={`btn-secondary text-sm ${className}`}
          aria-label={`Share ${title}`}
          aria-expanded={showMenu}
          aria-haspopup="menu"
        >
          <ShareIcon />
          <span className="ml-1.5">Share</span>
        </button>
        {showMenu && (
          <SocialShareMenu
            url={url}
            title={title}
            description={description}
            onClose={handleCloseMenu}
          />
        )}
      </div>
    );
  }

  // Default: secondary variant
  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleShare}
        className={`btn-secondary text-sm ${className}`}
        aria-label={`Share ${title}`}
        aria-expanded={showMenu}
        aria-haspopup="menu"
      >
        Share
      </button>
      {showMenu && (
        <SocialShareMenu
          url={url}
          title={title}
          description={description}
          onClose={handleCloseMenu}
        />
      )}
    </div>
  );
}
